import { useClientLogger } from '@coolblue-development/next-config/client';
import { getAllSelectedInstallationServices } from '@coolblue-development/next-installation-services/client';
import {
  addRegularProductsToMandy,
  addSecondChanceProductsToMandy,
  ProductCategory
} from '@coolblue-development/next-shopping-cart/actions';
import { CROSS_SELL_CHARGER_FIELD_NAME } from '@features/product/components/OrderBlock/UsbCCharger/constants';
import { ProductPlusPromotion } from '@features/product/typings';
import { addProductToShoppingCart } from '@utils/nextShoppingCartActions/addProductToShoppingCart';

import { ShoppingCartData, TradeInType } from './types';

type InstallationServiceCartItem = {
  productId: string;
  accessoryId: string;
};

type SetIsLoading = React.Dispatch<React.SetStateAction<boolean>>;

export const onSubmitForm = async (
  formElement: HTMLFormElement,
  tradeIn: TradeInType,
  trackingEvent: () => void,
  setIsLoading: SetIsLoading,
  productId: number,
  quantity: number,
  logger: ReturnType<typeof useClientLogger>,
  secondChanceProductId?: string,
  secondChanceRedirectUrl?: string,
  addToCartRedirectUrl?: string,
  freeWithPromotion?: ProductPlusPromotion
) => {
  const formData = new FormData(formElement);

  const installationServices = getAllSelectedInstallationServices(formData);
  const accessoryPromotion = formData.getAll('accessoryPromotion')[0]?.toString();
  const tradeInValue = tradeIn.brandId > 0 ? tradeIn : undefined;
  const crossSellChargerProductId = formData.get(CROSS_SELL_CHARGER_FIELD_NAME);

  if (secondChanceProductId) {
    return handleSecondChanceProductAction(
      Number(secondChanceProductId),
      installationServices as InstallationServiceCartItem[],
      trackingEvent,
      setIsLoading,
      logger,
      secondChanceRedirectUrl || ''
    );
  }

  // This condition will be removed when Mandy supports accessory promotions and trade-ins
  if (accessoryPromotion || tradeInValue) {
    const shoppingCartData = mapShoppingCartData(
      installationServices as InstallationServiceCartItem[],
      accessoryPromotion,
      tradeInValue,
      quantity
    );

    return handleAddToCartAction(productId, shoppingCartData, trackingEvent, setIsLoading, logger);
  }

  const subItems = [];

  if (freeWithPromotion) {
    subItems.push({
      productId: freeWithPromotion.id.toString(),
      quantity: 1,
      productCategory: ProductCategory.REGULAR,
      discount: {
        discountReason: freeWithPromotion.type,
        discountPercentage: 100.0,
        conditions: {
          requiredProductId: freeWithPromotion.id.toString(),
          // temporary hardcoded discountExpiresAt value
          // need to be replaced with real value from backend when available
          discountExpiresAt: '2029-12-31T23:59:59.000Z'
        }
      }
    });
  }

  const addedProducts = [
    {
      productId: productId.toString(),
      quantity,
      ...(installationServices.length > 0 && { installationServices }),
      ...(subItems.length > 0 && { subItems })
    }
  ];

  if (crossSellChargerProductId) {
    addedProducts.push({
      productId: crossSellChargerProductId.toString(),
      quantity: 1
    });
  }

  return handleMandyAddToCartAction(
    addedProducts,
    trackingEvent,
    setIsLoading,
    logger,
    addToCartRedirectUrl
  );
};

async function handleSecondChanceProductAction(
  secondChanceProductId: number,
  installationServices: InstallationServiceCartItem[],
  trackingEvent: () => void,
  setIsLoading: SetIsLoading,
  logger: ReturnType<typeof useClientLogger>,
  secondChanceRedirectUrl: string
): Promise<void> {
  try {
    await addSecondChanceProductsToMandy({
      products: [
        {
          productId: secondChanceProductId.toString(),
          ...(installationServices.length > 0 && { installationServices })
        }
      ]
    });

    trackingEvent();

    if (secondChanceRedirectUrl) {
      window.location.assign(secondChanceRedirectUrl);
      return;
    }
  } catch (error) {
    logger.error(`Failed to add second-chance-product to shopping cart`, error as Error);
  } finally {
    setIsLoading(false);
  }
}

async function handleMandyAddToCartAction(
  addedProducts: Parameters<typeof addRegularProductsToMandy>[0]['products'],
  trackingEvent: () => void,
  setIsLoading: SetIsLoading,
  logger: ReturnType<typeof useClientLogger>,
  addToCartRedirectUrl?: string
): Promise<void> {
  try {
    await addRegularProductsToMandy({
      products: addedProducts
    });

    trackingEvent();

    if (addToCartRedirectUrl) {
      window.location.assign(addToCartRedirectUrl);
      return;
    }
  } catch (error) {
    logger.error(`Failed to add product to shopping cart`, error as Error);
  } finally {
    setIsLoading(false);
  }
}

// This function will be removed when Mandy supports accessory promotions and trade-ins
async function handleAddToCartAction(
  productId: number,
  shoppingCartData: ShoppingCartData,
  trackingEvent: () => void,
  setIsLoading: SetIsLoading,
  logger: ReturnType<typeof useClientLogger>
): Promise<void> {
  try {
    const response = await addProductToShoppingCart({
      productId,
      params: shoppingCartData,
      revalidate: false
    });
    trackingEvent();

    const redirectUrl = response?.data?.redirectUrl;

    if (redirectUrl) {
      window.location.assign(redirectUrl);
      return;
    }
  } catch (error) {
    logger.error(`Failed to add product to shopping cart`, error as Error);
  } finally {
    setIsLoading(false);
  }
}

function mapShoppingCartData(
  installationServices: InstallationServiceCartItem[],
  accessoryPromotion: string | undefined,
  tradeInValue: TradeInType | undefined,
  quantity: number
): ShoppingCartData {
  const installationServiceProductIds = installationServices.map(service => service.productId);
  const installationServiceAccessory = installationServices
    .map(service => Number(service.accessoryId))
    .find(id => id > 0); // In the legacy shopping cart only one accessory is supported

  const shoppingCartData: ShoppingCartData = {};

  if (installationServices.length) {
    shoppingCartData.installationServices = installationServiceProductIds;
  }

  if (installationServiceAccessory) {
    shoppingCartData.installationServiceAccessory = installationServiceAccessory;
  }

  if (accessoryPromotion) {
    shoppingCartData.accessoryPromotionProductId = +accessoryPromotion;
  }

  if (tradeInValue) {
    shoppingCartData.tradeIn = tradeInValue;
  }

  if (quantity > 1) {
    shoppingCartData.quantity = quantity;
  }

  return shoppingCartData;
}
